
import React from 'react';
import AnimatedCard from '@/components/ui/AnimatedCard';
import { Tag, Apple, Store, Globe } from 'lucide-react';
import { Project } from '@/types/project';

interface MockupProps {
  children: React.ReactNode;
}

const PhoneMockup: React.FC<MockupProps> = ({ children }) => (
  <div className="relative w-[220px] h-[450px] lg:w-[280px] lg:h-[570px] mx-auto transform -mt-32 lg:-mt-48">
    <div className="absolute inset-0 rounded-[2rem] lg:rounded-[3rem] border-[8px] lg:border-[12px] border-gray-900 bg-gray-900 shadow-2xl"></div>
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-[20px] lg:h-[24px] bg-gray-900 rounded-b-xl lg:rounded-b-2xl z-10"></div>
    <div className="absolute inset-[8px] lg:inset-[12px] overflow-hidden rounded-[1.5rem] lg:rounded-[2.25rem]">
      {children}
    </div>
  </div>
);

const LaptopMockup: React.FC<MockupProps> = ({ children }) => (
  <div className="relative w-full max-w-3xl mx-auto aspect-[20/11] -mt-16 lg:-mt-24">
    {/* Main body */}
    <div className="absolute inset-0 rounded-xl bg-[#323232] shadow-2xl">
      {/* Screen bezel */}
      <div className="absolute inset-[3px] rounded-lg bg-[#272727] border-[0.5px] border-[#424242]">
        {/* Screen */}
        <div className="absolute inset-[2px] overflow-hidden rounded-lg bg-black">
          {children}
        </div>
      </div>
    </div>


    {/* Bottom section */}
    <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 w-[40%] h-[12px] bg-[#323232] rounded-b-xl">
      {/* Hinge */}
      
    </div>
  </div>
);

interface ProjectCardProps {
  project: Project;
  index: number;
  onMouseEnter: (id: string) => void;
  onMouseLeave: () => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  index,
  onMouseEnter,
  onMouseLeave,
}) => {
  const [logoError, setLogoError] = React.useState(false);

  return (
    <div className="relative">
      <AnimatedCard 
        className={`overflow-visible h-full hover-lift bg-gradient-to-br ${project.themeColor} relative`}
        animationDelay={index * 100}
        onMouseEnter={() => onMouseEnter(project.id)}
        onMouseLeave={onMouseLeave}
      >
        <div 
          className="absolute inset-0 opacity-10"
          style={{ backgroundImage: project.bgPattern }}
        />
        
        <div className="relative z-10 p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 text-center lg:text-left">
              <div className="flex items-center justify-center lg:justify-start gap-3 mb-6">
                <span className="bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-md text-sm font-bold text-white">
                  {project.industry}
                </span>
              </div>

              <div className="flex items-center justify-center lg:justify-start gap-4 mb-4">
                {project.logo && !logoError && (
                  <img 
                    src={project.logo} 
                    alt={`${project.title} logo`}
                    className="w-12 h-12 rounded-xl bg-white/10"
                    onError={() => setLogoError(true)}
                  />
                )}
                <h3 className="text-3xl font-bold text-white">{project.title}</h3>
              </div>
              
              <p className="text-white/90 text-lg mb-6">{project.description}</p>
              
              <div className="flex flex-wrap justify-center lg:justify-start gap-2 mb-4">
                {project.features.map((feature, i) => (
                  <span 
                    key={i}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-md text-sm font-medium text-white"
                  >
                    {feature}
                  </span>
                ))}
              </div>


              <div className="flex flex-wrap justify-center lg:justify-start gap-3">
                {project.stores?.apple && (
                  <a 
                    href={project.stores.apple}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-md text-sm font-bold text-white transition-colors"
                  >
                    <img src="/apple-store-white.svg" alt="Apple Store" className="w-6 h-8" />
                  </a>
                )}
                {project.stores?.google && (
                  <a 
                    href={project.stores.google}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-md text-sm font-bold text-white transition-colors"
                  >
                    <img src="/google-play-white.svg" alt="Google Play" className="w-6 h-8"/>
                  </a>
                )}
                {project.stores?.web && (
                  <a 
                    href={project.stores.web}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-md text-sm font-bold text-white transition-colors"
                  >
                    <Globe width={24} height={32} />
                  </a>
                )}
              </div>
            </div>

            <div className="order-1 lg:order-2 flex justify-center items-center">
              {project.type === 'mobile' ? (
                <PhoneMockup>
                  {project.video ? (
                    <video
                      src={project.video}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-fill"
                    />
                  ) : (
                    <img 
                      src={project.previewImage || "https://via.placeholder.com/280x570"} 
                      alt={`${project.title} preview`}
                      className="w-full h-full object-cover"
                    />
                  )}
                </PhoneMockup>
              ) : (
                <LaptopMockup>
                  {project.video ? (
                    <video
                      src={project.video}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-auto h-full object-fill"
                    />
                  ) : (
                    <img 
                      src={project.previewImage || "https://via.placeholder.com/800x450"} 
                      alt={`${project.title} preview`}
                      className="w-full h-full object-cover"
                    />
                  )}
                </LaptopMockup>
              )}
            </div>
          </div>
        </div>
      </AnimatedCard>
    </div>
  );
};

export default ProjectCard; 
